import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule, TablePageEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitchChangeEvent, ToggleSwitchModule } from 'primeng/toggleswitch';
import { AVAILABLE_PAGE_SIZE } from '@client/app/shared-modules/api/constants/page-size.constant';
import { PerformanceProfilesRepo } from '@client/app/feature-modules/admin/modules/admin-performance-profiles/services/performance-profiles.repo';
import {
    PageProfileDataInterface,
} from '@client/app/feature-modules/admin/modules/admin-performance-profiles/types/performance-profile.type';
import { MonEventTriggerModule } from '@monsido/angular-shared-components';
import { ActionTriggerService } from '@client/app/shared-modules/tracking/action-trigger/action-trigger.service';
import { ParamService } from '@monsido/core/param/param.service';
import { MenuItem } from 'primeng/api';
import { GlobalToastMessageService } from '@client/app/shared-modules/global-toast/services/global-toast-message.service';
import { FormsModule } from '@angular/forms';
import { MenuModule } from 'primeng/menu';
import { CardModule } from 'primeng/card';
import { PagePerformanceService } from '@client/app/modules/page-performance/services/page-performance/page-performance.service';
import { DialogService } from 'primeng/dynamicdialog';
import { RestorePerformanceProfileDialogComponent } from '@client/app/feature-modules/admin/modules/admin-performance-profiles/components/restore-performance-profile-dialog/restore-performance-profile-dialog.component';
import { ProfileLimitReachedDialogComponent } from '@client/app/feature-modules/admin/modules/admin-performance-profiles/components/profile-limit-reached-dialog/profile-limit-reached-dialog.component';
import { ArchivePerformanceProfileDialogComponent } from '@client/app/feature-modules/admin/modules/admin-performance-profiles/components/archive-performance-profile-dialog/archive-performance-profile-dialog.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { StateService } from '@uirouter/core';
import { AdminLayoutService } from '@client/app/feature-modules/admin/services/admin-layout.service';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';
import { TableWrapperComponent } from '@client/app/shared-modules/table/components/table-wrapper.component';
import { TableCellTemplateDirective } from '@client/app/shared-modules/table/directives/table-cell-template.directive';

type PerformanceProfileWithActions = PageProfileDataInterface & {
    actionItems: MenuItem[];
};

@Component({
    selector: 'mon-admin-performance-profiles',
    templateUrl: './admin-performance-profiles.component.html',
    standalone: true,
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        MonEventTriggerModule,
        TooltipModule,
        ToggleSwitchModule,
        FormsModule,
        MenuModule,
        CardModule,
        TranslocoModule,
        PageLayoutComponent,
        TableWrapperComponent,
        TableCellTemplateDirective,
    ],
    providers: [
        PerformanceProfilesRepo,
        DialogService,
    ],
    styleUrls: ['./admin-performance-profiles.component.scss'],
})
export class AdminPerformanceProfilesComponent implements OnInit {
    readonly AVAILABLE_PAGE_SIZE = AVAILABLE_PAGE_SIZE;
    private readonly translocoService = inject(TranslocoService);

    readonly TABLE_SCHEMA = TableWrapperComponent.createTableSchema<PerformanceProfileWithActions>({
        NAME: {
            label: this.translocoService.translate('adminPerformanceProfiles.table.name'),
            id: 'name',
            type: 'property',
            widthClass: 'tw-w-5/12',
        },
        DESCRIPTION: {
            label: this.translocoService.translate('adminPerformanceProfiles.table.description'),
            id: 'description',
            type: 'property',
            widthClass: 'tw-w-5/12',
        },
        STATUS: {
            label: this.translocoService.translate('adminPerformanceProfiles.table.status'),
            id: 'status',
            type: 'custom',
        },
        ACTIONS: {
            label: this.translocoService.translate('adminPerformanceProfiles.table.actions'),
            id: 'actions',
            type: 'custom',
            actionColumn: true,
        },
    });

    @ViewChild(TableWrapperComponent) private readonly tableWrapper?: TableWrapperComponent<PerformanceProfileWithActions>;

    performanceProfiles: PerformanceProfileWithActions[] = [];
    currentPage = 1;
    perPage = AVAILABLE_PAGE_SIZE[0];
    total = 0;
    loading = true;
    isArchived = false;
    activeProfilesCount = 0;
    availableProfiles = 0;

    private menuTriggerElement: HTMLElement | null = null;

    constructor (
        private readonly performanceProfilesRepo: PerformanceProfilesRepo,
        private readonly pagePerformanceService: PagePerformanceService,
        private readonly actionTriggerService: ActionTriggerService,
        private readonly paramService: ParamService,
        private readonly destroyRef: DestroyRef,
        private readonly dialogService: DialogService,
        private readonly globalToastMessageService: GlobalToastMessageService,
        private readonly stateService: StateService,
        private readonly adminLayoutService: AdminLayoutService,
    ) {}

    ngOnInit (): void {
        this.setBreadcrumbs();
        const { page_size, page, archived } = this.paramService.getParams();
        this.isArchived = archived === 'true';
        this.getPerformanceProfiles(
            parseInt(page),
            parseInt(page_size),
            this.isArchived,
        ).then();

        const deregisterHook = this.paramService.subscribeParamsChangeOnCurrentState((params) => {
            this.isArchived = params.archived === 'true';
            this.getPerformanceProfiles(
                parseInt(params.page),
                parseInt(params.page_size),
                this.isArchived,
            ).then();
        });

        this.destroyRef.onDestroy(() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            deregisterHook();
        });
    }

    public onMenuHide (): void {
        const el = document.activeElement;
        this.menuTriggerElement = (el instanceof HTMLElement) ? el : null;
    }

    setBreadcrumbs (): void {
        this.adminLayoutService.setBreadcrumbs({
            uiStates: [
                {
                    label: this.translocoService.translate('admin.performanceProfiles.table.breadcrumbLabel'),
                    state: 'base.customer.admin.performance-profiles',
                },
            ],
        });
    }

    async getPerformanceProfiles (
        page: number,
        pageSize: number,
        archived: boolean = this.isArchived,
    ): Promise<void> {
        this.loading = true;
        const [profilesResponse, activeProfilesCount] = await Promise.all([
            this.performanceProfilesRepo.getAll({
                page: page,
                page_size: pageSize,
                archived,
            }),
            this.getActiveProfilesCount(),
        ]);

        if (!profilesResponse) {
            this.loading = false;
            return;
        }
        this.perPage = profilesResponse.perPage || 0;
        this.currentPage = profilesResponse.currentPage || 1;
        this.total = profilesResponse.total || 0;

        const performanceProfiles: PerformanceProfileWithActions[] = [];

        for (const profile of profilesResponse) {
            let actionItems: MenuItem[];
            if (profile.archived_at) {
                actionItems = [
                    {
                        label: this.translocoService.translate('adminPerformanceProfiles.table.actions.restoreProfile'),
                        icon: 'pi pi-replay',
                        command: () => this.restoreProfile(profile),
                    },
                ];
            } else {
                actionItems = [
                    {
                        label: this.translocoService.translate('adminPerformanceProfiles.table.actions.editProfile'),
                        icon: 'pi pi-pencil',
                        command: () => this.editProfile(profile),
                    },
                    {
                        label: this.translocoService.translate('adminPerformanceProfiles.table.actions.archiveProfile'),
                        icon: 'pi pi-trash',
                        command: () => this.archiveProfile(profile),
                    },
                ];
            }
            performanceProfiles.push({
                ...profile,
                actionItems,
            });
        }

        this.performanceProfiles = performanceProfiles;
        this.activeProfilesCount = activeProfilesCount;
        this.availableProfiles = this.getAvailableProfiles();
        this.loading = false;
    }

    private async reload (): Promise<void> {
        const { page_size, page } = this.paramService.getParams();
        return this.getPerformanceProfiles(
            parseInt(page),
            parseInt(page_size),
        );
    }

    onPageChange (pageChangeEvent: TablePageEvent) {
        const { first, rows } = pageChangeEvent;

        // Check if page size has changed
        const pageSizeChanged = rows !== this.perPage;

        // If page size changed, reset to page 1; otherwise calculate current page
        const pageNumber = pageSizeChanged ? 1 : Math.floor((first || 0) / (rows || 1)) + 1;

        this.paramService.updateParams({
            page: pageNumber,
            page_size: rows,
        });
    }

    onToggleShowOnlyArchivedProfiles (event: ToggleSwitchChangeEvent): void {
        this.actionTriggerService.track({
            action: 'admin-performance-profiles-toggle-archived-profiles-filter-btn',
        });

        // start from the first page when toggling archived profiles
        const { page_size } = this.paramService.getParams();
        this.paramService.updateParams({
            page: 1,
            page_size: page_size,
            archived: event.checked ? 'true' : 'false',
        });
    }

    addProfile (): void {
        this.actionTriggerService.track({
            action: 'admin-performance-profiles-add-profile-btn',
        });
        if (this.getAvailableProfiles() > 0) {
            this.stateService.go('base.customer.admin.performance-profile-form');
        } else {
            this.showProfileLimitReachedDialog();
        }
    }

    editProfile (profile: PageProfileDataInterface): void {
        this.actionTriggerService.track({
            action: 'admin-performance-profiles-edit-profile-btn',
        });
        this.stateService.go('base.customer.admin.performance-profile-form', { id: profile.id });
    }

    restoreProfile (profile: PageProfileDataInterface) {
        this.actionTriggerService.track({
            action: 'admin-performance-profiles-restore-profile-btn',
        });
        const dialogConfig = {
            header: this.translocoService.translate('adminPerformanceProfiles.restoreDialog.header'),
            data: profile,
            baseZIndex: 10000,
            showHeader: true,
            closable: true,
            dismissableMask: true,
            width: '600px',
        };
        const dialogRef = this.dialogService.open(RestorePerformanceProfileDialogComponent, dialogConfig);

        dialogRef.onClose.subscribe(async (result: boolean) => {
            this.menuTriggerElement?.focus();
            this.menuTriggerElement = null;
            if (!result) {
                return;
            }

            this.loading = true;
            try {
                this.activeProfilesCount = await this.getActiveProfilesCount();
                this.availableProfiles = this.getAvailableProfiles();
                if (this.availableProfiles === 0) {
                    this.showProfileLimitReachedDialog();
                } else {
                    await this.performanceProfilesRepo.unarchiveProfile(profile);
                    this.globalToastMessageService.info(
                        this.translocoService.translate('adminPerformanceProfiles.restoreDialog.restoreSuccessToast{{profileName}}', {
                            profileName: profile.name,
                        }),
                    );

                    await this.reload();
                }
            } catch (err) {
                this.globalToastMessageService.error(
                    this.translocoService.translate('adminPerformanceProfiles.restoreDialog.restoreFailedToast{{profileName}}', {
                        profileName: profile.name,
                    }),
                );
            } finally {
                this.loading = false;
            }
        });
    }

    archiveProfile (profile: PageProfileDataInterface) {
        this.tableWrapper?.trackAction(this.performanceProfiles, this.performanceProfiles.find(p => p.id === profile.id) ?? profile as unknown as PerformanceProfileWithActions, 'archive', () => true);
        this.actionTriggerService.track({
            action: 'admin-performance-profiles-archive-profile-btn',
        });

        const dialogConfig = {
            header: this.translocoService.translate('adminPerformanceProfiles.archiveDialog.header'),
            data: profile,
            baseZIndex: 10000,
            showHeader: true,
            closable: true,
            dismissableMask: true,
            width: '600px',
        };

        const dialogRef = this.dialogService.open(ArchivePerformanceProfileDialogComponent, dialogConfig);

        dialogRef.onClose.subscribe(async (result: boolean) => {
            this.menuTriggerElement?.focus();
            this.menuTriggerElement = null;
            if (!result) {
                return;
            }

            this.loading = true;
            try {
                await this.performanceProfilesRepo.archiveProfile(profile);
                this.globalToastMessageService.info(
                    this.translocoService.translate('adminPerformanceProfiles.archiveDialog.archiveSuccessToast{{profileName}}', {
                        profileName: profile.name,
                    }),
                );
                await this.reload();
            } catch (err) {
                this.globalToastMessageService.error(
                    this.translocoService.translate('adminPerformanceProfiles.archiveDialog.archiveFailedToast{{profileName}}', {
                        profileName: profile.name,
                    }),
                );
            } finally {
                this.loading = false;
            }
        });
    }

    showProfileLimitReachedDialog () {
        const dialogConfig = {
            // header: 'Maximum profiles reached',
            header: this.translocoService.translate('adminPerformanceProfiles.profileLimitDialog.header'),
            baseZIndex: 10000,
            showHeader: true,
            closable: true,
            dismissableMask: true,
            width: '900px',
        };
        const dialogRef = this.dialogService.open(ProfileLimitReachedDialogComponent, dialogConfig);

        dialogRef.onClose.subscribe(async (result: boolean) => {
            if (!result) {
                return;
            }

            // await this.pagePerformanceService.requestMoreProfiles('Contact me about more performance profiles');
            await this.pagePerformanceService.requestMoreProfiles(
                this.translocoService.translate('adminPerformanceProfiles.profileLimitDialog.requestProfileMessage'),
            );
            this.globalToastMessageService.info(
                this.translocoService.translate('adminPerformanceProfiles.profileLimitDialog.requestProfileSuccessToast'),
            );
        });
    }

    private async getActiveProfilesCount (): Promise<number> {
        const profilesResponse = await this.performanceProfilesRepo.getAll({
            page: 1,
            page_size: 1,
            archived: false,
        });
        return profilesResponse ? profilesResponse.total || 0 : 0;
    }

    private getAvailableProfiles (): number {
        const available = this.pagePerformanceService.getMaxProfileCount() - (this.activeProfilesCount || 0);
        return available > 0 ? available : 0;
    }
}
